import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Driver, Vehicle } from '../models/fleet.model';
import { Page } from '../../models/page.model';

export interface APIResponseWrapperClass<T> {
  success: boolean;
  data: T;
  message: string;
  responseCode?: string;
}

export interface APIListWrapper<T> {
  hasMore: boolean;
  limit: number;
  count: number;
  offset: number;
  items: T[];
  links?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class FleetService {
  private http = inject(HttpClient);
  private driverApiUrl = `${environment.restApiUrl}driver-endpoint`;
  private vehicleApiUrl = `${environment.restApiUrl}vehicle-endpoint`;
  private vehicleParamsApiUrl = `${environment.restApiUrl}vehicle-params-endpoint`;
  private aiServiceDriverUrl = `${environment.aiServiceApiUrl}driver`;
  private aiServiceVehicleUrl = `${environment.aiServiceApiUrl}vehicle`;

  // ********** AI EXTRACTION ENDPOINTS **********

  /** Extract info from driver documents via AI Service using a URL */
  extractDriverInfoByUrl(imageUrl: string, type: 'residence' | 'license'): Observable<any> {
    const params = new HttpParams()
      .set('imageUrl', imageUrl)
      .set('type', type);
    return this.http.post<any>(`${this.aiServiceDriverUrl}/extract-info-url`, null, { params });
  }

  /** Extract info from vehicle registration documents via AI Service using a URL */
  extractVehicleInfoByUrl(imageUrl: string): Observable<any> {
    const params = new HttpParams()
      .set('imageUrl', imageUrl);
    return this.http.post<any>(`${this.aiServiceVehicleUrl}/extract-info-url`, null, { params });
  }

  /** Extract info from driver documents via AI Service */
  extractDriverInfo(file: File, type: 'residence' | 'license'): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    return this.http.post<any>(`${this.aiServiceDriverUrl}/extract-info`, formData);
  }

  /** Extract info from vehicle registration documents via AI Service */
  extractVehicleInfo(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.aiServiceVehicleUrl}/extract-info`, formData);
  }

  // ********** PARAMETER ENDPOINTS **********

  getVehicleMakes(tree: boolean = false): Observable<any[]> {
    return this.http.get<any[]>(`${this.vehicleParamsApiUrl}/makes?tree=${tree}`);
  }

  getVehicleModels(tree: boolean = false): Observable<any[]> {
    return this.http.get<any[]>(`${this.vehicleParamsApiUrl}/models?tree=${tree}`);
  }

  getVehicleYears(): Observable<any[]> {
    return this.http.get<any[]>(`${this.vehicleParamsApiUrl}/modelyears`);
  }

  getVehicleTypes(tree: boolean = false): Observable<any[]> {
    return this.http.get<any[]>(`${this.vehicleParamsApiUrl}/types?tree=${tree}`);
  }

  getVehicleConfigs(): Observable<any[]> {
    return this.http.get<any[]>(`${this.vehicleParamsApiUrl}/configurations`);
  }

  getVehicleCapacities(): Observable<any[]> {
    return this.http.get<any[]>(`${this.vehicleParamsApiUrl}/capacities`);
  }

  ensureVehicleMake(name: string): Observable<any> {
    return this.http.post<any>(`${this.vehicleParamsApiUrl}/ensure-make?name=${name}`, {});
  }

  ensureVehicleModel(makeId: number, name: string): Observable<any> {
    return this.http.post<any>(`${this.vehicleParamsApiUrl}/ensure-model?makeId=${makeId}&name=${name}`, {});
  }

  ensureVehicleYear(modelId: number, year: number): Observable<any> {
    return this.http.post<any>(`${this.vehicleParamsApiUrl}/ensure-year?modelId=${modelId}&year=${year}`, {});
  }

  // ********** DRIVER ENDPOINTS **********

  /** List drivers by transportation agency */
  getDriversByTransportationAgency(agencyId: number, page: number = 0, pageSize: number = 100): Observable<APIListWrapper<Driver>> {
    const params = new HttpParams()
      .set('transportationAgencyId', agencyId)
      .set('page', page)
      .set('pageSize', pageSize);
    return this.http.get<APIListWrapper<Driver>>(`${this.driverApiUrl}/drivers-by-transportation-agency`, { params });
  }

  /** Find specific driver by firebase ID */
  getDriverById(driverId: string, vehicleInfoRequired: boolean = true): Observable<APIResponseWrapperClass<Driver>> {
    const params = new HttpParams()
      .set('driverId', driverId)
      .set('vehicleInfoRequired', vehicleInfoRequired.toString());
    return this.http.get<APIResponseWrapperClass<Driver>>(`${this.driverApiUrl}/driver`, { params });
  }

  /** Find specific drivers by database IDs */
  getDriversByIds(driverIds: number[], vehicleInfoRequired: boolean = true, cultureCode: string = 'en-US'): Observable<APIResponseWrapperClass<Driver[]>> {
    const params = new HttpParams()
      .set('driverDatabaseIds', driverIds.join(','))
      .set('vehicleInfoRequired', vehicleInfoRequired.toString())
      .set('cultureCode', cultureCode);
    return this.http.get<APIResponseWrapperClass<Driver[]>>(`${this.driverApiUrl}/drivers`, { params });
  }

  /** Create a generic driver/freelance driver */
  createDriver(driver: Driver): Observable<APIResponseWrapperClass<boolean | Driver>> {
    return this.http.post<APIResponseWrapperClass<Driver>>(`${this.driverApiUrl}/driver`, driver);
  }

  /** Update an existing driver */
  updateDriver(driver: Driver): Observable<APIResponseWrapperClass<Driver>> {
    return this.http.put<APIResponseWrapperClass<Driver>>(`${this.driverApiUrl}/driver`, driver);
  }

  /** Mobilize: Attach driver to vehicle */
  assignVehicleToDriver(driverId: string, vehicleId: number): Observable<APIResponseWrapperClass<Driver>> {
    const params = new HttpParams()
      .set('driverId', driverId)
      .set('vehicleId', vehicleId.toString());
    return this.http.put<APIResponseWrapperClass<Driver>>(`${this.driverApiUrl}/driver-current-vehicle`, null, { params });
  }

  // ********** VEHICLE ENDPOINTS **********

  /** List vehicles by transportation agency */
  getVehiclesByTransportationAgency(transportationAgencyId: number, page: number = 0, pageSize: number = 100): Observable<APIListWrapper<Vehicle>> {
    const params = new HttpParams()
      .set('transportationAgencyId', transportationAgencyId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<APIListWrapper<Vehicle>>(`${this.vehicleApiUrl}/vehicles-by-transportation-agency`, { params });
  }

  /** Find specific vehicles by list of vehicle IDs */
  getVehiclesByIds(vehicleIds: number[], cultureCode: string = 'en-US'): Observable<APIResponseWrapperClass<Vehicle[]>> {
    const params = new HttpParams()
      .set('cultureCode', cultureCode)
      .set('vehicleIds', vehicleIds.join(','));
    return this.http.get<APIResponseWrapperClass<Vehicle[]>>(`${this.vehicleApiUrl}/vehicles`, { params });
  }

  /** Find specific vehicle by ID */
  getVehicleById(vehicleId: number, cultureCode: string = 'en-US'): Observable<APIResponseWrapperClass<Vehicle>> {
    const params = new HttpParams()
      .set('cultureCode', cultureCode)
      .set('vehicleIds', vehicleId.toString());
    return this.http.get<APIResponseWrapperClass<Vehicle[]>>(`${this.vehicleApiUrl}/vehicles`, { params })
      .pipe(
        map((res: any) => {
          return {
            ...res,
            data: (res.data && res.data.length > 0) ? res.data[0] : null
          };
        })
      );
  }

  /** Create a vehicle */
  createVehicle(vehicle: Vehicle): Observable<APIResponseWrapperClass<Vehicle>> {
    return this.http.post<APIResponseWrapperClass<Vehicle>>(`${this.vehicleApiUrl}/vehicles`, vehicle);
  }

  /** Update a vehicle */
  updateVehicle(vehicle: Vehicle): Observable<APIResponseWrapperClass<Vehicle>> {
    return this.http.put<APIResponseWrapperClass<Vehicle>>(`${this.vehicleApiUrl}/vehicles`, vehicle);
  }
}
